/**
 * Formats a record object to a specific structure.
 * @param {Object} record - The record object to format.
 * @param {string} record.facility_name - The name of the facility.
 * @param {number} record.facility_id - The ID of the facility.
 * @param {number} record.asset_id - The ID of the asset.
 * @param {string} record.asset_name - The name of the asset.
 * @param {string} record.start_date - The start date of the record.
 * @param {string} record.end_date - The end date of the record.
 * @param {string} record.changeover_cause - The cause of the changeover.
 * @param {string} record.cause_code - The code of the cause.
 * @param {number} record.changeover_duration - The duration of the changeover.
 * @param {number} record.expected_duration - The expected duration of the changeover.
 * @param {number} record.exceeded_duration - The exceeded duration of the changeover.
 * @param {string} record.starting_product - The next product after the changeover.
 * @param {string} record.starting_sku - The SKU of the next product.
 * @param {string} record.starting_product_category - The category of the next product.
 * @param {Object} record.starting_product_properties - The properties of the next product.
 * @param {string} record.ending_product - The previous product before the changeover.
 * @param {string} record.ending_sku - The SKU of the previous product.
 * @param {string} record.ending_product_category - The category of the previous product.
 * @param {Object} record.ending_product_properties - The properties of the previous product.
 * @param {string} record.start - The start time of the report.
 * @param {string} record.end - The end time of the report.
 * @returns {Object} The formatted record object.
 */
function formatRecord(record) {
    return {
        facilityName: record.facility_name,
        facilityId: record.facility_id,
        assetId: record.asset_id,
        assetName: record.asset_name,
        startDate: record.start_date,
        endDate: record.end_date,
        changeoverCause: record.changeover_cause,
        causeCode: record.cause_code,
        changeoverDuration: record.changeover_duration,
        expectedDuration: record.expected_duration,
        exceededDuration: record.exceeded_duration,
        nextProductName: record.starting_product,
        nextProductSku: record.starting_sku,
        nextProductCategory: record.starting_product_category,
        nextProductProperties: record.starting_product_properties,
        previousProductName: record.ending_product,
        previousProductSku: record.ending_sku,
        previousProductCategory: record.ending_product_category,
        previousProductProperties: record.ending_product_properties,
        start: record.start,
        end: record.end
    };
}

/**
 * Builds the changeover report data from the given records.
 * @param {Array<Object>} records - The array of record objects to process.
 * @returns {Array<Object>} The processed report data.
 */
export function buildChangeoverReport(records) {
    const reportData = new Map();

    records.forEach(record => {
        const formattedRecord = formatRecord(record);

        const assetId = formattedRecord.assetId;

        // Handle asset data
        const assetData =
            reportData.get(assetId) || createAssetData(formattedRecord);

        // Handle product view data
        const productKey = `${formattedRecord.previousProductSku}-${formattedRecord.nextProductSku}`;
        const productRow =
            assetData.productView.get(productKey) ||
            createRowData(formattedRecord);
        const updatedProductRow = updateRowData(productRow, formattedRecord);
        // add an ID, next, and previous product name to the product row
        updatedProductRow.id = productKey;
        updatedProductRow.nextProductName = formattedRecord.nextProductName;
        updatedProductRow.previousProductName =
            formattedRecord.previousProductName;
        assetData.productView.set(productKey, updatedProductRow);

        // Handle cause view data
        const causeKey = formattedRecord.causeCode;
        const causeRow =
            assetData.causeView.get(causeKey) || createRowData(formattedRecord);
        const updatedCauseRow = updateRowData(causeRow, formattedRecord);
        // add an ID and cause name to the cause row
        updatedCauseRow.id = causeKey;
        updatedCauseRow.causeName = formattedRecord.changeoverCause;
        updatedCauseRow.expectedDuration = formattedRecord.expectedDuration;
        assetData.causeView.set(causeKey, updatedCauseRow);

        // Handle property view data

        // Only create property view data if the previous and next product categories are the same
        if (
            formattedRecord.nextProductCategory ===
            formattedRecord.previousProductCategory
        ) {
            const previousProperties =
                formattedRecord.previousProductProperties;
            const nextProperties = formattedRecord.nextProductProperties;

            // Create all combinations of previous and next values for each property
            for (const key in nextProperties) {
                if (Object.hasOwn(previousProperties, key)) {
                    const nextValue = nextProperties[key];
                    const previousValue = previousProperties[key];
                    // Skip if previous or next properties are empty
                    if (!nextValue || !previousValue) {
                        continue;
                    }
                    const propertyKey = `${nextValue}-${previousValue}`;
                    const propertyRow =
                        assetData.propertyView.get(propertyKey) ||
                        createRowData(formattedRecord);
                    const updatedPropertyRow = updateRowData(
                        propertyRow,
                        formattedRecord
                    );
                    // Set ID, previous, and next properties
                    updatedPropertyRow.id = propertyKey;
                    updatedPropertyRow.previousProperty = `${key}: ${previousValue}`;
                    updatedPropertyRow.nextProperty = `${key}: ${nextValue}`;
                    updatedPropertyRow.productCategory =
                        formattedRecord.nextProductCategory;
                    assetData.propertyView.set(propertyKey, updatedPropertyRow);
                }
            }
        }

        // update report data
        reportData.set(assetId, assetData);
    });

    // convert each view map to an array as the q-table component expects an array
    reportData.forEach(assetData => {
        assetData.propertyView = Array.from(assetData.propertyView.values());
        assetData.causeView = Array.from(assetData.causeView.values());
        assetData.productView = Array.from(assetData.productView.values());
    });

    return Array.from(reportData.values());
}

/**
 * Updates the view data with the provided record.
 * @param {Object} rowData - The current view data object.
 * @returns {Object} The updated view data object.
 */
function updateRowData(rowData, record) {
    rowData.eventsCount++;
    rowData.totalAccumulatedDuration += record.changeoverDuration;
    rowData.totalAccumulatedExpectedDuration += record.expectedDuration;
    rowData.totalLoss =
        rowData.totalAccumulatedDuration -
        rowData.totalAccumulatedExpectedDuration;
    rowData.averageDuration =
        rowData.totalAccumulatedDuration / rowData.eventsCount;
    rowData.averageLoss = rowData.totalLoss / rowData.eventsCount;
    return rowData;
}

/**
 * Generates the initial values for an asset based on the provided record.
 * @param {Object} record - The record containing asset information.
 * @returns {Object} The initial values for the asset.
 */
function createAssetData(record) {
    return {
        assetId: record.assetId,
        assetName: record.assetName,
        propertyView: new Map(),
        causeView: new Map(),
        productView: new Map()
    };
}

/**
 * Generates the initial values based on the provided record.
 * @param {Object} record - The record containing product information.
 * @returns {Object} The initial values for the product view.
 */
function createRowData(record) {
    return {
        averageDuration: 0,
        averageLoss: 0,
        eventsCount: 0,
        totalAccumulatedDuration: 0,
        totalAccumulatedExpectedDuration: 0,
        totalLoss: 0
    };
}
